import {
  type FailedExperienceResponse,
  type RuntimeExperienceResponse,
} from '@bbkFunnelComponents/funnel-types';

/**
 * The loader's failure modes, made explicit.
 *
 * Today every one of these arrives as a bare `Error` carrying only a message,
 * so a caller cannot tell an expired token -- recoverable by refreshing --
 * from a malformed session key, which is not. Worse, the message is sometimes
 * empty, and the hosted funnel's `??` fallback does not catch that because an
 * empty string is not nullish, so the user is left on a loading state forever.
 */
export type ExperienceLoadFailureKind =
  'token_expired' | 'session_not_found' | 'network' | 'malformed_response';

export class ExperienceLoadError extends Error {
  readonly kind: ExperienceLoadFailureKind;

  /** Absent for `network`, where no response was received. */
  readonly status?: number;

  constructor(
    kind: ExperienceLoadFailureKind,
    message: string,
    status?: number
  ) {
    super(message);
    this.name = 'ExperienceLoadError';
    this.kind = kind;
    this.status = status;
  }
}

export type LoadExperienceOptions = {
  /**
   * Explicit, rather than sniffed from the URL. The hosted funnel reads it
   * from path index 2, which is also wrong for `/api/v1/session/{key}`.
   */
  sessionKey: string;
  /** Defaults to the current origin. */
  baseUrl?: string;
  fetch?: typeof fetch;
  /**
   * Abandons the fetch when the caller decides it has waited long enough. An
   * embedded run needs this because its overlay has no dismiss control, so a
   * request that stalls rather than fails leaves the visitor with no way out.
   * An aborted fetch surfaces as a `network` failure like any other.
   */
  signal?: AbortSignal;
};

const REFRESH_INTERVAL_MS = 1000 * 60 * 60 * 24;

const kindForStatus = (status: number): ExperienceLoadFailureKind => {
  if (status === 401 || status === 403 || status === 410) {
    return 'token_expired';
  }
  if (status === 404 || status === 400) {
    return 'session_not_found';
  }
  return 'network';
};

export const experienceUrl = ({
  sessionKey,
  baseUrl = '',
}: LoadExperienceOptions): string =>
  `${baseUrl}/api/v1/session/${sessionKey}?load_experience`;

export type FetchedExperience = {
  experience: RuntimeExperienceResponse;
  /**
   * Exposed for callers that need transport metadata; the hosted funnel reads
   * `X-Geo-Resolved-Currency` off it. Most callers want `loadExperience`.
   */
  response: Response;
};

/**
 * Fetches the experience for a session. Unlike the hosted funnel's inline
 * version this sets no globals and touches no DOM, so it is callable from a
 * merchant's page.
 */
export const fetchExperience = async (
  options: LoadExperienceOptions
): Promise<FetchedExperience> => {
  const doFetch = options.fetch ?? fetch;

  let response: Response;
  try {
    response = await doFetch(experienceUrl(options), {
      signal: options.signal,
    });
  } catch (cause) {
    throw new ExperienceLoadError(
      'network',
      cause instanceof Error && cause.message
        ? cause.message
        : 'The experience could not be reached.'
    );
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new ExperienceLoadError(
      'malformed_response',
      'The experience response could not be parsed.',
      response.status
    );
  }

  if (!response.ok) {
    const { message } = (payload ?? {}) as FailedExperienceResponse;
    throw new ExperienceLoadError(
      kindForStatus(response.status),
      // Never empty, which is what strands the hosted funnel today.
      message || `The experience could not be loaded (${response.status}).`,
      response.status
    );
  }

  return { experience: payload as RuntimeExperienceResponse, response };
};

export const loadExperience = async (
  options: LoadExperienceOptions
): Promise<RuntimeExperienceResponse> =>
  (await fetchExperience(options)).experience;

export type ExperienceLoader = {
  load(): Promise<RuntimeExperienceResponse>;
  refresh(): Promise<RuntimeExperienceResponse>;
  /**
   * Returns the canceller. The hosted funnel registers an equivalent interval
   * it never clears; handing back a handle is what makes that fixable.
   *
   * `onError` is required rather than optional. A refresh loop runs unattended
   * for the life of the page, so there is no caller for whom ignoring failures
   * is the right default, and an optional handler here means every failed tick
   * leaves a rejected promise nobody observes.
   */
  startAutoRefresh(
    onRefresh: (experience: RuntimeExperienceResponse) => void,
    onError: (error: unknown) => void,
    intervalMs?: number
  ): () => void;
};

export const createExperienceLoader = (
  options: LoadExperienceOptions
): ExperienceLoader => {
  const fetchOnce = () => loadExperience(options);

  return {
    load: fetchOnce,
    refresh: fetchOnce,
    startAutoRefresh: (
      onRefresh,
      onError,
      intervalMs = REFRESH_INTERVAL_MS
    ) => {
      let cancelled = false;
      /**
       * Ticks are numbered so a slow response cannot overwrite a newer one
       * that already landed. Reachable whenever a request outlives the
       * interval, and the payload carries the authenticity token, so losing
       * the race means reinstating a token that is closer to expiry.
       */
      let newestTick = 0;

      const handle = setInterval(() => {
        const tick = ++newestTick;
        const isStale = () => cancelled || tick !== newestTick;

        fetchOnce().then(
          (experience) => {
            if (isStale()) {
              return;
            }
            onRefresh(experience);
          },
          (error: unknown) => {
            if (isStale()) {
              return;
            }
            onError(error);
          }
        );
      }, intervalMs);

      return () => {
        cancelled = true;
        clearInterval(handle);
      };
    },
  };
};
